import { NgZone } from '@angular/core';
import { Subscription } from 'rxjs';
import { ATTENDANCE_STATUS_CHANGED_SSE_EVENTS } from '../../shared/constants/realtime.constants';
import { RealtimeEventsService } from '../../services/realtime-events.service';

export interface AttendanceStatusChangedPayload {
  employee_id: string;
  work_status?: 'checked_in' | 'checked_out' | 'on_break' | string;
  presence?: 'online' | 'offline' | string;
  action?: string | null;
  location_id?: string;
  admin_id?: string;
  attendance_id?: string;
}

export interface AttendanceStatusRealtimeConfig {
  /** Called inside `NgZone.run` when an attendance status event arrives. */
  onAttendanceStatusChanged: (payload: AttendanceStatusChangedPayload) => void;
}

/**
 * Normalize an SSE attendance payload. Requires employee_id plus work_status and/or presence.
 */
export function parseAttendanceStatusChangedPayload(
  data: any
): AttendanceStatusChangedPayload | null {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const row = data as Record<string, any>;
  const employeeId = String(row['employee_id'] ?? row['employeeId'] ?? '').trim();
  const workStatus = String(row['work_status'] ?? row['workStatus'] ?? '').trim();
  const presence = String(row['presence'] ?? '').trim();
  if (!employeeId || (!workStatus && !presence)) {
    return null;
  }

  const payload: AttendanceStatusChangedPayload = {
    employee_id: employeeId,
  };

  if (workStatus) {
    payload.work_status = workStatus;
  }
  if (presence) {
    payload.presence = presence;
  }

  const action = row['action'];
  if (action !== null && action !== undefined && String(action).trim() !== '') {
    payload.action = String(action);
  }

  const locationId = String(row['location_id'] ?? row['locationId'] ?? '').trim();
  if (locationId) {
    payload.location_id = locationId;
  }

  const adminId = String(row['admin_id'] ?? row['adminId'] ?? '').trim();
  if (adminId) {
    payload.admin_id = adminId;
  }

  const attendanceId = String(row['attendance_id'] ?? row['attendanceId'] ?? '').trim();
  if (attendanceId) {
    payload.attendance_id = attendanceId;
  }

  return payload;
}

/**
 * Bind `attendance_status_changed` from the shared SSE stream so the employee
 * list can patch Attendance without polling / hard reload.
 *
 * Does not disconnect the stream — the caller only manages this Subscription.
 */
export function bindAttendanceStatusSseEvent(
  realtime: RealtimeEventsService,
  ngZone: NgZone,
  config: AttendanceStatusRealtimeConfig
): Subscription {
  const sub = new Subscription();

  if (!realtime.isConnected() || realtime.getAuthMode() !== 'user') {
    realtime.connectUser();
  }

  for (const eventName of ATTENDANCE_STATUS_CHANGED_SSE_EVENTS) {
    sub.add(
      realtime.on(eventName).subscribe((data) => {
        const payload = parseAttendanceStatusChangedPayload(data);
        if (!payload) {
          return;
        }
        ngZone.run(() => config.onAttendanceStatusChanged(payload));
      })
    );
  }

  return sub;
}
